import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Emails } from '@app/components/atoms/chip/chip.component';
import {
  EMPTY_STRING,
  GET_CHAPTER_ACCESS_BY_PRAGMATIC_ID_PATH,
  GET_PENDING_SKILL_TESTS_PATH,
  getFilterByTechnologyAndSpecialityByChapterId,
  getGraphAssessmentById,
  getPragmaticChapterDetailsByEmail,
  getPragmaticChapterDetailsByEmailAndChapterId,
  getPragmaticChapterDetailsSyncByEmail,
  getPragmaticProfileByEmail,
  getPragmaticProfileById,
  getPragmaticProfileByToken,
  getPragmaticValorationDataByEmail,
  getPragmaticValorationDataById,
  getPragmaticValorationDataByToken,
  getQueryParamsString,
  INITIAL_PAGE,
  PAGE_SIZE_DEFAULT,
  POST_SAVE_METADATA_ASSESSMENT,
  PragmaticServiceRoutes,
  responseStatus,
  SLASH_STRING,
  URL_QUERY_START,
} from '@app/constants';
import { SpecialitiesResponse } from '@app/interface/chapter-info.interface';
import { SaveMetadata } from '@app/interface/metadataPragmatic.interface';
import { ListInfo } from '@app/interface/pragmatic-from-chapter';
import { PragmaticUrlInform, UrlData } from '@app/interface/pragmatic-info';
import { PragmaticUserResponse } from '@app/interface/pragmatic-info.response';
import { PragmaticFromChapterResponse } from '@app/interface/pragmatics-from-chapters-response';
import { ServiceType } from '@app/interface/service';
import { ResponsePragmaticValorationData } from '@app/interface/valoration.interface';
import { environment } from '@environments/environment';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { doGetResponseType, GatewayService } from './gateway.service';

export type PragmaticRecordType = {
  profilePicture: string;
  name: string;
  lastName: string;
  email?: string;
  role: string;
  level: string;
  project: string;
  nextValidationDate: string;
  isTomorrow?: boolean;
  isToday?: boolean;
  isExpired?: boolean;
  isMainChapter?: boolean;
};
export type RequestPragmaticsResponse = {
  success: boolean;
  page: number;
  pragmatics: Array<PragmaticRecordType>;
  totalPages: number;
};

@Injectable({
  providedIn: 'root',
})
export class PragmaticsService {
  constructor(
    private readonly gateway: GatewayService,
    private readonly apollo: Apollo
  ) {}

  public chapterSelect = new BehaviorSubject<string>(EMPTY_STRING);
  originSelect = EMPTY_STRING;

  updateStatusOnboarding(pragmaticEmail: string) {
    return this.apollo.mutate({
      mutation: gql`
        mutation updateStatusOnboarding($pragmaticEmail: String!) {
          statusViewOnboarding: updatePragmaticViewOnboarding(
            pragmaticEmail: $pragmaticEmail
            viewOnboarding: true
          )
        }
      `,
      variables: {
        pragmaticEmail: pragmaticEmail,
      },
    });
  }

  requestChapterList(): Observable<any> {
    return from(
      this.gateway
        .doGet(PragmaticServiceRoutes.ChapterList, {}, ServiceType.Assessments)
        .then((response: doGetResponseType) => {
          if (response.success) {
            return {
              success: true,
              chapters: response.data,
            };
          }
          return {
            success: false,
          };
        })
    );
  }

  hasEvaTestPending(userEmail: string): Observable<any> {
    const queryParamsString = getQueryParamsString({
      email: userEmail,
    });
    const path = [GET_PENDING_SKILL_TESTS_PATH, URL_QUERY_START, queryParamsString].join(
      EMPTY_STRING
    );
    return from(
      this.gateway
        .doGet(path, {}, ServiceType.AssessmentsPA)
        .then((response: doGetResponseType) => {
          return response.data;
        })
    );
  }

  requestSeniority(): Observable<any> {
    return from(
      this.gateway
        .doGet(PragmaticServiceRoutes.SeniorityFilter, {}, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          if (response.success) {
            return {
              success: true,
              chapters: response.data,
            };
          }
          return {
            success: false,
          };
        })
    );
  }

  requestAssessmentUrl(userEmail: string, chapterId: string): Observable<any> {
    return from(
      this.gateway
        .doGet(
          PragmaticServiceRoutes.AssessmentUrl,
          { userEmail, chapterId },
          ServiceType.Assessments
        )
        .then((response: doGetResponseType) => {
          if (response.success) {
            return {
              success: true,
              chapters: response.data,
            };
          }
          return {
            success: false,
          };
        })
    );
  }

  searchByEmail(
    term: string,
    enabled = true,
    page = INITIAL_PAGE.toString(),
    page_size = PAGE_SIZE_DEFAULT.toString()
  ): Observable<any> {
    const queryParamsString = getQueryParamsString({
      enabled: `${enabled}`,
      page,
      page_size,
      filter: term,
    });

    const path = [
      PragmaticServiceRoutes.AdministrationPragmaticList,
      URL_QUERY_START,
      queryParamsString,
    ].join(EMPTY_STRING);

    return from(
      this.gateway
        .doGet(path, {}, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          if (response.success) {
            const responseWithImages = response.data.data.map((emails: Emails) => {
              return {
                ...emails,
                imageUrl: `${environment.endpointMultimedia}/${emails.imageUrl}`,
              };
            });
            return {
              success: true,
              chapters: responseWithImages,
            };
          }
          return {
            success: false,
          };
        })
    );
  }

  sendReportToEmail(payLoad: object): Observable<any> {
    return from(
      this.gateway
        .doPost(PragmaticServiceRoutes.TrackingToolProcessData, payLoad, ServiceType.Assessments)
        .then((response: doGetResponseType) => {
          if (response.success) {
            return {
              success: true,
              validate: response.data,
            };
          }
          return {
            success: false,
          };
        })
    );
  }

  resendReportToEmail(payLoad: object): Observable<any> {
    return from(
      this.gateway
        .doPost(
          PragmaticServiceRoutes.TrackingToolResendAssessmentEmail,
          payLoad,
          ServiceType.Assessments
        )
        .then((response: doGetResponseType) => {
          if (response.success) {
            return {
              success: true,
              validate: response.data,
            };
          }
          return {
            success: false,
          };
        })
    );
  }

  saveMetadataPragmatic(payload: SaveMetadata): Observable<boolean> {
    const path = POST_SAVE_METADATA_ASSESSMENT;

    return from(
      this.gateway
        .doPost(path, payload, ServiceType.MapRatingService)
        .then((response: doGetResponseType) => {
          return response.success || response.status === HttpStatusCode.Ok;
        })
        .catch(() => {
          return false;
        })
    );
  }

  requestPragmaticUserInfobyToken(): Observable<PragmaticUserResponse> {
    const path = getPragmaticProfileByToken();

    return from(
      this.gateway
        .doGet<PragmaticUserResponse>(path, {}, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return {
              success: true,
              pragmatic_info: response.data,
            };
          }
          return {
            success: false,
          } as PragmaticUserResponse;
        })
    );
  }

  requestPragmaticUserInfoByEmail(email: string): Observable<PragmaticUserResponse> {
    const path = getPragmaticProfileByEmail(email);

    return from(
      this.gateway
        .doGet<PragmaticUserResponse>(path, {}, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return {
              success: true,
              pragmatic_info: response.data,
            };
          }
          return {
            success: false,
          } as PragmaticUserResponse;
        })
    );
  }

  requestPragmaticUserInfoById(id: number): Observable<PragmaticUserResponse> {
    const path = getPragmaticProfileById(id);

    return from(
      this.gateway
        .doGet<PragmaticUserResponse>(path, {}, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return {
              success: true,
              pragmatic_info: response.data,
            };
          }
          return { success: false } as PragmaticUserResponse;
        })
    );
  }

  requestPragmaticFromChapter(
    payload: ListInfo
  ): Observable<{ success: boolean; chapters?: PragmaticFromChapterResponse }> {
    return from(
      this.gateway
        .doPost(PragmaticServiceRoutes.ChapterUsers, payload, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return {
              success: true,
              chapters: response.data as PragmaticFromChapterResponse,
            };
          }
          return {
            success: false,
          };
        })
    );
  }

  requestGraphAssessment(chapterId: number | string): Observable<any> {
    const path = getGraphAssessmentById(`${chapterId}`);
    return from(
      this.gateway
        .doGet(path, {}, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          if (response.success) {
            return {
              success: true,
              chapters: response.data,
            };
          }
          return {
            success: false,
          };
        })
    );
  }

  requestPragmaticValorationData(email: string): Observable<ResponsePragmaticValorationData> {
    const path = getPragmaticValorationDataByEmail(email);
    return from(
      this.gateway
        .doGet<ResponsePragmaticValorationData>(path, {}, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return {
              success: true,
              valoration: response.data,
            };
          }
          return {
            success: false,
          } as ResponsePragmaticValorationData;
        })
    );
  }

  requestPragmaticValorationDataByToken(): Observable<ResponsePragmaticValorationData> {
    const path = getPragmaticValorationDataByToken();
    return from(
      this.gateway
        .doGet<ResponsePragmaticValorationData>(path, {}, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return {
              success: true,
              valoration: response.data,
            };
          }
          return { success: false } as ResponsePragmaticValorationData;
        })
    );
  }

  requestPragmaticValorationDataById(
    pragmaticId: number
  ): Observable<ResponsePragmaticValorationData> {
    const path = getPragmaticValorationDataById(pragmaticId);

    return from(
      this.gateway
        .doGet<ResponsePragmaticValorationData>(path, {}, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return {
              success: true,
              valoration: response.data,
            };
          }
          return { success: false } as ResponsePragmaticValorationData;
        })
    );
  }

  requestPragmaticUrlInform(email: string, chapterID: string): Observable<PragmaticUrlInform> {
    const path = getPragmaticChapterDetailsByEmailAndChapterId(email, chapterID);
    return from(
      this.gateway.doGet(path, {}, ServiceType.Assessments).then((response: doGetResponseType) => {
        if (response.success || response.status === HttpStatusCode.Ok) {
          return {
            success: true,
            urlData: response.data,
          };
        }
        return {
          success: false,
          urlData: response.data as UrlData,
        };
      })
    );
  }

  requestTechnologyAndSpecialityList(chapterId: number | string): Observable<any> {
    const path = getFilterByTechnologyAndSpecialityByChapterId(`${chapterId}`);
    return from(
      this.gateway
        .doGet(path, {}, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return {
              success: true,
              chapters: response.data,
            };
          }
          return {
            success: false,
          };
        })
    );
  }

  getChapterList(): Observable<any> {
    const queryParamsString = getQueryParamsString({
      enabled: 'true',
    });

    const path = [
      PragmaticServiceRoutes.AdministrationChaptersList,
      URL_QUERY_START,
      queryParamsString,
    ].join(EMPTY_STRING);

    return from(
      this.gateway
        .doGet(path, {}, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return {
              success: true,
              chapters: response.data,
            };
          }
          return {
            success: false,
          };
        })
    );
  }

  requestUpdateInfoPragmatico(dataUpdate: any, pragmaticEmail: string): Observable<any> {
    const path = getPragmaticChapterDetailsByEmail(pragmaticEmail);
    return from(
      this.gateway
        .doPut(path, dataUpdate, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          if (response.success || response.status === responseStatus.success) {
            return {
              success: true,
              chapters: response.data,
            };
          }
          return {
            success: false,
          };
        })
    );
  }

  requestPragmaticSyncFileChapter(email: string): Observable<any> {
    const path = getPragmaticChapterDetailsSyncByEmail(email);
    return from(
      this.gateway
        .doPatch(path, {}, ServiceType.Assessments)
        .then((response: doGetResponseType) => {
          if (response.success) {
            return {
              success: true,
              validate: response.data,
            };
          }
          return {
            success: false,
          };
        })
    );
  }

  getSpecialitiesAndTechnologies({
    chapterId,
    specialityId,
  }: {
    chapterId: number;
    specialityId?: number;
  }): Observable<SpecialitiesResponse> {
    const path = [
      PragmaticServiceRoutes.AdministrationChapterSpecialityTechnology,
      SLASH_STRING,
      chapterId.toString(),
      specialityId ? `?specialityId=${specialityId}` : EMPTY_STRING,
    ].join(EMPTY_STRING);

    return from(
      this.gateway
        .doGet(path, {}, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          return response.data;
        })
    );
  }

  getChapterAccessByPragmaticId(pragmaticId: number): Observable<number[]> {
    const path = `${GET_CHAPTER_ACCESS_BY_PRAGMATIC_ID_PATH}/${pragmaticId}`;

    return from(
      this.gateway
        .doGet<{
          data: number[];
        }>(path, {}, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          if (response.success && response.data) {
            return response.data;
          }
          return [];
        })
    );
  }
}
